import { useParams } from 'react-router'
import { useSessionStorage } from 'usehooks-ts'

import CaretDownOutlinedIcon from '@ant-design/icons/CaretDownOutlined'
import CaretUpOutlinedIcon from '@ant-design/icons/CaretUpOutlined'

import Box from '@mui/material/Box'
import CardActionArea from '@mui/material/CardActionArea'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Stack from '@mui/material/Stack'
import { alpha, useTheme } from '@mui/material/styles'
import Typography from '@components/_mui/Typography'

export default function Section({
  id,
  label,
  count,
  action,
  icon,
  expandable = true,
  collapsed = false,
  hideCollapsedHint = false,
  children,
}) {
  const { id: paramId } = useParams()
  const theme = useTheme()
  const [expanded, setExpanded] = useSessionStorage(id || ['section', paramId, label].filter(Boolean).join('-'), !collapsed)

  const Icon = expanded ? CaretUpOutlinedIcon : CaretDownOutlinedIcon

  return (
    <Stack component="section" id={id || `soap-section-${label.toLowerCase()}`}>
      {expandable ? (
        <CardActionArea component="div" onClick={() => setExpanded((o) => !o)} sx={styles.header}>
          <Header label={label} count={count} icon={icon} />
          {action}
          <Icon style={{ color: theme.palette.primary.main }} />
        </CardActionArea>
      ) : (
        <Box sx={styles.header}>
          <Header label={label} count={count} icon={icon} />
          {action}
        </Box>
      )}
      {expandable ? (
        <>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ pt: 1 }}>{children}</Box>
          </Collapse>
          {!hideCollapsedHint && (
            <Collapse in={!expanded} unmountOnExit>
              <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', p: 1 }}>Expand to view more information.</Typography>
            </Collapse>
          )}
        </>
      ) : (
        <Box sx={{ pt: 1 }}>{children}</Box>
      )}
    </Stack>
  )
}

function Header({ label, count, icon }) {
  return (
    <>
      {icon ? <Box sx={styles.icon}>{icon}</Box> : <Box sx={styles.accent} />}
      <Typography variant="h5" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      {count != null && count !== '' && <Chip label={count} color="default" size="small" />}
      <Box sx={{ flex: 1 }} />
    </>
  )
}

const styles = {
  header: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 1.5,
    py: 1,
    borderRadius: 1,
    backgroundColor: alpha(theme.palette.secondary.main, 0.06),
  }),
  accent: {
    width: 4,
    height: 24,
    borderRadius: 2,
    backgroundColor: 'primary.main',
    flexShrink: 0,
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'primary.main',
    flexShrink: 0,
  },
}
